import React, { useState } from 'react';
import { Play, RotateCcw, Clock, CheckCircle2, ChevronRight, X, AlertCircle, Sparkles, BookOpen, Layers } from 'lucide-react';
import { useAuth } from './AuthBridge';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface ContinueLastQuizCardProps {
  onNavigate: (page: string) => void;
  variant?: 'banner' | 'compact' | 'hero-cta';
  className?: string;
}

export const ContinueLastQuizCard: React.FC<ContinueLastQuizCardProps> = ({ 
  onNavigate, 
  variant = 'banner',
  className = '' 
}) => {
  const { user, activeCert, updateUser } = useAuth();
  const tokens = useThemeTokens();
  const [isDiscarding, setIsDiscarding] = useState(false);

  // Check if authenticated user has an active unfinished session
  const currentSession = user?.currentSession;
  const sessionData = currentSession?.session;
  const questions = sessionData?.questions;

  const hasUnfinishedSession = !!(
    user &&
    currentSession &&
    sessionData &&
    Array.isArray(questions) &&
    questions.length > 0 &&
    !currentSession.isSubmitted
  );

  if (!hasUnfinishedSession || !questions) {
    return null;
  }

  const totalQuestions = questions.length;
  const userAnswers = currentSession.userAnswers || {};
  const answeredCount = Object.keys(userAnswers).length;
  const currentIndex = Math.min(totalQuestions - 1, Math.max(0, currentSession.currentIndex || 0));
  const currentQuestionNumber = currentIndex + 1;
  const progressPercent = Math.min(100, Math.round((answeredCount / totalQuestions) * 100));
  
  const examTitle = sessionData.examConfig?.vendor || 
                    (currentSession.examMode === 'practice' ? 'Practice Quiz' : 'Timed State Board Simulation');
  const targetCertId = currentSession.certId || activeCert?.id || 'cna';

  const timeRemainingSeconds = currentSession.timeRemaining;
  const hasTimer = timeRemainingSeconds !== undefined && timeRemainingSeconds > 0 && currentSession.examMode !== 'practice';
  const timeRemainingFormatted = hasTimer 
    ? `${Math.floor(timeRemainingSeconds / 60)}m ${timeRemainingSeconds % 60}s remaining`
    : null;

  const handleContinue = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // Redirect directly to the unfinished session
    const targetPath = targetCertId === activeCert?.id 
      ? 'exam?resume=true' 
      : `/${targetCertId}/exam?resume=true`;
    onNavigate(targetPath);
  };

  const handleDiscard = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to discard this unfinished quiz session? Your saved progress for this attempt will be cleared.')) {
      setIsDiscarding(true);
      try {
        if (user?.id) {
          try {
            localStorage.removeItem(`active_exam_session_${user.id}`);
          } catch (err) {
            // ignore
          }
        }
        await updateUser({ currentSession: null });
      } catch (err) {
        console.error('Failed to discard unfinished session:', err);
      } finally {
        setIsDiscarding(false);
      }
    }
  };

  // Compact Inline Button (ideal for hero button rows)
  if (variant === 'hero-cta') {
    return (
      <button
        onClick={handleContinue}
        id="continue-last-quiz-hero-cta"
        className={`flex items-center gap-2.5 px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-black text-[10px] uppercase tracking-wider shadow-md hover:shadow-lg transition-all active:scale-95 group/btn ${className}`}
        title={`Continue ${examTitle} (Q${currentQuestionNumber}/${totalQuestions})`}
      >
        <RotateCcw size={14} className="group-hover/btn:rotate-180 transition-transform duration-500 text-emerald-200" />
        <span className="font-extrabold">Continue Last Quiz</span>
        <span className="bg-emerald-800/60 px-2 py-0.5 rounded-full text-[9px] font-black text-emerald-100 border border-emerald-400/30">
          Q{currentQuestionNumber}/{totalQuestions}
        </span>
      </button>
    );
  }

  // Compact Pill / Chip variant
  if (variant === 'compact') {
    return (
      <div 
        onClick={handleContinue}
        className={`inline-flex items-center gap-3 p-2 pr-4 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/80 rounded-2xl cursor-pointer hover:border-emerald-400 transition-all shadow-xs group ${className}`}
      >
        <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-xs shadow-xs group-hover:scale-105 transition-transform">
          <RotateCcw size={14} />
        </div>
        <div className="text-left">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-extrabold text-emerald-900 dark:text-emerald-200">Continue In-Progress Quiz</span>
            <span className="text-[9px] font-bold text-emerald-700 dark:text-emerald-400">({progressPercent}%)</span>
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium">Question {currentQuestionNumber} of {totalQuestions}</p>
        </div>
        <ChevronRight size={14} className="text-emerald-600 group-hover:translate-x-1 transition-transform ml-auto" />
      </div>
    );
  }

  // Full High-Impact Banner Variant (default)
  return (
    <div 
      id="continue-last-quiz-banner"
      className={`relative overflow-hidden rounded-2xl md:rounded-3xl border transition-all duration-200 shadow-sm hover:shadow-md ${
        tokens.isClinical
          ? 'bg-gradient-to-r from-[#0B2942]/95 to-[#113859] border-emerald-500/30 text-white'
          : tokens.isWarmAcademic
          ? 'bg-gradient-to-r from-[#E6F4F1] via-teal-50/70 to-emerald-50/50 border-[#0D6E6E]/30 text-slate-900 dark:bg-slate-900 dark:text-white dark:border-teal-800/60'
          : tokens.isStructuredCourse
          ? 'bg-gradient-to-r from-blue-900 to-indigo-950 border-blue-400/30 text-white'
          : 'bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-transparent border-emerald-200 dark:border-emerald-900/60 text-slate-900 dark:text-white dark:bg-slate-900/90'
      } ${className}`}
    >
      {/* Subtle background glow */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-500/10 blur-3xl pointer-events-none rounded-full" />

      <div className="p-5 sm:p-6 md:p-7 relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-5">
        
        {/* Left column: Session info & progress indicator */}
        <div className="space-y-3 flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-600 text-white text-[10px] font-black uppercase tracking-wider rounded-full shadow-xs">
              <RotateCcw size={11} className="animate-spin-slow" />
              <span>Unfinished Quiz Detected</span>
            </span>

            {hasTimer && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-bold rounded-full border border-amber-400/30">
                <Clock size={11} />
                <span>{timeRemainingFormatted}</span>
              </span>
            )}

            <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">
              Last active: {currentSession.lastSaved ? new Date(currentSession.lastSaved).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Recently'}
            </span>
          </div>

          <div>
            <h3 className="text-lg md:text-xl font-black tracking-tight flex items-center gap-2">
              <span>{examTitle}</span>
              <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-950/60 px-2 py-0.5 rounded-md border border-emerald-200 dark:border-emerald-800/60">
                Question {currentQuestionNumber} of {totalQuestions}
              </span>
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 font-medium mt-1">
              You answered <strong>{answeredCount}</strong> of <strong>{totalQuestions}</strong> questions ({progressPercent}% complete). Click below to resume right where you left off.
            </p>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1.5 max-w-xl">
            <div className="flex justify-between text-[11px] font-bold text-slate-500 dark:text-slate-400">
              <span>Progress</span>
              <span>{answeredCount}/{totalQuestions} Answered ({progressPercent}%)</span>
            </div>
            <div className="h-2.5 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden p-0.5">
              <div 
                className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500" 
                style={{ width: `${Math.max(5, progressPercent)}%` }} 
              />
            </div>
          </div>
        </div>

        {/* Right column: Action Buttons */}
        <div className="flex flex-row md:flex-col sm:items-center md:items-end gap-3 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-slate-200 dark:border-slate-800">
          <button
            onClick={handleContinue}
            id="continue-last-quiz-main-button"
            className="flex-1 md:flex-none flex items-center justify-center gap-2.5 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg hover:shadow-xl transition-all duration-150 active:scale-95 group/continue cursor-pointer"
          >
            <Play size={14} className="fill-current group-hover/continue:translate-x-0.5 transition-transform" />
            <span>Continue Last Quiz</span>
            <ChevronRight size={14} className="group-hover/continue:translate-x-1 transition-transform" />
          </button>

          <button
            onClick={handleDiscard}
            disabled={isDiscarding}
            className="px-3.5 py-2 text-[11px] font-bold text-slate-500 hover:text-rose-600 dark:text-slate-400 dark:hover:text-rose-400 transition-colors flex items-center gap-1.5 active:scale-95"
            title="Discard this unfinished session and start fresh"
          >
            <X size={13} />
            <span>{isDiscarding ? 'Discarding...' : 'Discard Session'}</span>
          </button>
        </div>

      </div>
    </div>
  );
};
